<template>
  <p class="body-2 mb-16 flex gap-6">
    <ActiveShippingProviders :weekend-delivery-configurations />
  </p>

  <ManageWeekendDeliveryDrawer>
    <template #body="{ close }">
      <p class="heading-2 mb-6">
        {{ i18n(translations.singleMarketWeekendDeliveryDrawerHeadline) }}
      </p>
      <p class="mb-24">
        {{ i18n(translations.singleMarketWeekendDeliveryDrawerInstruction) }}
      </p>
      <RevForm
        :id="formId"
        :initial-values="carrierWeekendDeliveryConfigurationsByCarrierByDay"
        :name="formId"
        noValidate
        @submit="updateWeekendDeliveryConfigurations($event, close)"
      >
        <template #default="{ values }">
          <RevCard class="p-12">
            <table class="w-full text-left">
              <thead>
                <tr>
                  <th class="p-16">
                    {{
                      i18n(
                        translations.singleMarketWeekendDeliveryConfigurationFormTableHeadline,
                      )
                    }}
                  </th>
                  <th class="p-16">
                    <RevCheckbox
                      id="select-all--SATURDAY"
                      :checked="isAllCarrierSelected(values, 'SATURDAY')"
                      data-test="select-all--SATURDAY"
                      @update:model-value="
                        (newValue) =>
                          selectAllCarrier(values, 'SATURDAY', newValue)
                      "
                    >
                      <template #label>
                        {{
                          i18n(
                            translations.singleMarketWeekendDeliveryConfigurationFormTableHeadSelectAll,
                          )
                        }}
                      </template>
                    </RevCheckbox>
                  </th>
                  <th class="p-16">
                    <RevCheckbox
                      id="select-all--SUNDAY"
                      :checked="isAllCarrierSelected(values, 'SUNDAY')"
                      data-test="select-all--SUNDAY"
                      @update:model-value="
                        (newValue) =>
                          selectAllCarrier(values, 'SUNDAY', newValue)
                      "
                    >
                      <template #label>
                        {{
                          i18n(
                            translations.singleMarketWeekendDeliveryConfigurationFormTableHeadSelectAll,
                          )
                        }}
                      </template>
                    </RevCheckbox>
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="carrierCodename of Object.keys(values)"
                  :key="carrierCodename"
                  class="border-static-default-low border-t-1"
                >
                  <td class="px-16 py-36">
                    {{ carrierCodename }}
                  </td>
                  <td class="px-16 py-36">
                    <RevCheckbox
                      :id="`select-carrier--${carrierCodename}--SATURDAY`"
                      v-model="values[carrierCodename].SATURDAY.workingDay"
                      :data-test="`select-carrier--${carrierCodename}--SATURDAY`"
                      @update:model-value="watchChanges(values)"
                    >
                      <template #label>
                        {{ getWeekday('SATURDAY') }}
                      </template>
                    </RevCheckbox>
                  </td>
                  <td class="px-16 py-36">
                    <RevCheckbox
                      :id="`select-carrier--${carrierCodename}--SUNDAY`"
                      v-model="values[carrierCodename].SUNDAY.workingDay"
                      :data-test="`select-carrier--${carrierCodename}--SUNDAY`"
                      @update:model-value="watchChanges(values)"
                    >
                      <template #label>
                        {{ getWeekday('SUNDAY') }}
                      </template>
                    </RevCheckbox>
                  </td>
                </tr>
              </tbody>
            </table>
          </RevCard>
        </template>
      </RevForm>
    </template>
    <template #footer>
      <div class="flex justify-end">
        <RevButton
          :disabled="!isConfigurationUpdated || isSubmittingForm"
          :form="formId"
          :loading="isSubmittingForm"
          type="submit"
          variant="primary"
        >
          {{
            i18n(
              translations.singleMarketWeekendDeliveryConfigurationFormSubmitButton,
            )
          }}
        </RevButton>
      </div>
    </template>
    <template #trigger="{ open }">
      <RevButton
        :icon="IconChevronRight"
        size="small"
        variant="secondary"
        @click="open"
      >
        {{
          i18n(
            isWeekendDeliveryConfigured
              ? translations.singleMarketWeekendDeliveryTriggerUpdate
              : translations.singleMarketWeekendDeliveryTriggerSetUp,
          )
        }}
      </RevButton>
    </template>
  </ManageWeekendDeliveryDrawer>
</template>

<script setup lang="ts">
import { computed, ref, useId } from 'vue'

import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { groupBy } from '@backmarket/utils/object/groupBy'
import { isEqual } from '@backmarket/utils/object/isEqual'
import { RevButton } from '@ds/components/Button'
import { RevCard } from '@ds/components/Card'
import { RevCheckbox } from '@ds/components/Checkbox'
import { RevForm } from '@ds/components/Form'
import { setFormValues } from '@ds/components/Form/Form.actions'
import { IconChevronRight } from '@ds/icons/IconChevronRight'

import type {
  WeekendDay,
  WeekendDeliveryConfigurationsGroupedBy,
} from '../../ManageWeekendDelivery.types'
import { useGetWeekday } from '../../composables/useGetWeekday'
import { useUpdateCarrierWeekendDeliveriesConfiguration } from '../../composables/useUpdateCarrierWeekendDeliveriesConfiguration'
import ManageWeekendDeliveryDrawer from '../ManageWeekendDeliveryDrawer/ManageWeekendDeliveryDrawer.vue'

import translations from './SingleMarketWeekendDelivery.translations'
import type {
  SingleMarketWeekendDeliveryProps,
  WeekendDeliveryConfigurationByCarrierCodenameByWeekendDay,
} from './SingleMarketWeekendDelivery.types'
import ActiveShippingProviders from './components/ActiveShippingProviders/ActiveShippingProviders.vue'

const i18n = useI18n()
const formId = useId()
const getWeekday = useGetWeekday()
const updateCarrierWeekendDeliveriesConfiguration =
  useUpdateCarrierWeekendDeliveriesConfiguration()

const { isWeekendDeliveryConfigured, weekendDeliveryConfigurations } =
  defineProps<SingleMarketWeekendDeliveryProps>()
const emit = defineEmits(['updated'])

const isConfigurationUpdated = ref(false)
const isSubmittingForm = ref(false)

const carrierWeekendDeliveryConfigurationsByCarrierByDay =
  computed<WeekendDeliveryConfigurationByCarrierCodenameByWeekendDay>(() => {
    const carrierWeekendDeliveryConfigurationsByCarrier = groupBy(
      weekendDeliveryConfigurations,
      ({ carrierCodename }) => carrierCodename,
    ) as WeekendDeliveryConfigurationsGroupedBy<'carrierCodename'>

    return Object.entries(carrierWeekendDeliveryConfigurationsByCarrier).reduce(
      (acc, [carrierCodename, configurations]) => {
        return {
          ...acc,
          [carrierCodename]: {
            SATURDAY: configurations.find(
              (configuration) => configuration.day === 'SATURDAY',
            ),
            SUNDAY: configurations.find(
              (configuration) => configuration.day === 'SUNDAY',
            ),
          },
        }
      },
      {},
    )
  })

function isAllCarrierSelected(
  formValues: WeekendDeliveryConfigurationByCarrierCodenameByWeekendDay,
  weekendDay: WeekendDay,
) {
  return Object.values(formValues).every(
    (configurations) => configurations[weekendDay].workingDay,
  )
}

function selectAllCarrier(
  formValues: WeekendDeliveryConfigurationByCarrierCodenameByWeekendDay,
  weekendDay: WeekendDay,
  isSelectingAllCarrier: boolean | Array<string>,
) {
  const newValues = Object.entries(formValues).reduce(
    (acc, [carrierCodename, configurationByDay]) => {
      return {
        ...acc,
        [carrierCodename]: {
          ...configurationByDay,
          [weekendDay]: {
            ...configurationByDay[weekendDay],
            workingDay: !!isSelectingAllCarrier,
          },
        },
      }
    },
    {},
  )
  setFormValues(formId, newValues)
  watchChanges(newValues)
}

function watchChanges(
  formValues: WeekendDeliveryConfigurationByCarrierCodenameByWeekendDay,
) {
  isConfigurationUpdated.value = !isEqual(
    formValues,
    carrierWeekendDeliveryConfigurationsByCarrierByDay.value,
  )
}

async function updateWeekendDeliveryConfigurations(
  formValues: WeekendDeliveryConfigurationByCarrierCodenameByWeekendDay,
  onSubmit: () => void,
) {
  isSubmittingForm.value = true
  const flattenedUpdatedWeekendDeliveryConfigurations = Object.values(
    formValues,
  ).flatMap((configurationByDay) => Object.values(configurationByDay))

  try {
    await updateCarrierWeekendDeliveriesConfiguration(
      weekendDeliveryConfigurations,
      flattenedUpdatedWeekendDeliveryConfigurations,
    )
    emit('updated')
    onSubmit()
  } catch (error) {
    isSubmittingForm.value = false
  }
}
</script>
